import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import EasyCropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import {
  ASSIGNMENT_COVER_ASPECT,
  ASSIGNMENT_COVER_IMAGE_MAX,
  type AssignmentCoverImage,
} from '../../types/assignmentPointsNote';
import {
  deleteAssignmentCoverImage,
  newAssignmentCoverImageId,
  uploadAssignmentCoverImage,
} from '../../services/assignmentCoverImageStorageService';
import {
  invalidateAssignmentCoverCache,
  putAssignmentCoverCacheFromBlob,
} from '../../utils/assignmentCoverImageCache';
import CachedAssignmentCoverImage from '../common/CachedAssignmentCoverImage';
import {
  cropRectToWebp,
  readFileAsDataURL,
  type CropAreaPixels,
} from '../../utils/imageCompress';
import { readClipboardImageBlob } from '../../utils/slideImageCompress';

const Cropper = EasyCropper as unknown as React.ComponentType<{
  image?: string;
  crop: { x: number; y: number };
  zoom: number;
  aspect: number;
  cropShape?: 'rect' | 'round';
  showGrid?: boolean;
  onCropChange: (location: { x: number; y: number }) => void;
  onZoomChange?: (zoom: number) => void;
  onCropComplete?: (croppedArea: CropAreaPixels, croppedAreaPixels: CropAreaPixels) => void;
}>;

export type AssignmentCoverImageModalProps = {
  open: boolean;
  coverImages: AssignmentCoverImage[];
  selectedCoverImageId?: string;
  onClose: () => void;
  /** 라이브러리에서 선택 */
  onSelectExisting: (imageId: string) => void;
  /** 새 업로드 후 라이브러리·선택 반영 */
  onUploaded: (image: AssignmentCoverImage) => void;
  /** 라이브러리에서 삭제 */
  onDeleteLibraryImage: (imageId: string) => void;
};

const OUT_W = 288;
const OUT_H = Math.round(OUT_W / ASSIGNMENT_COVER_ASPECT);

const AssignmentCoverImageModal: React.FC<AssignmentCoverImageModalProps> = ({
  open,
  coverImages,
  selectedCoverImageId,
  onClose,
  onSelectExisting,
  onUploaded,
  onDeleteLibraryImage,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cropPixelsRef = useRef<CropAreaPixels | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setIsDragging(false);
      setRawImageSrc('');
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setIsSaving(false);
      setDeletingId(null);
      setError('');
      cropPixelsRef.current = null;
    }
  }, [open]);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('이미지 파일은 10MB 이하만 업로드할 수 있습니다.');
      return;
    }
    if (coverImages.length >= ASSIGNMENT_COVER_IMAGE_MAX) {
      setError(`과제 이미지는 최대 ${ASSIGNMENT_COVER_IMAGE_MAX}개까지 등록할 수 있습니다. 기존 이미지를 삭제한 뒤 올려 주세요.`);
      return;
    }
    setError('');
    const dataUrl = await readFileAsDataURL(file);
    setRawImageSrc(dataUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    cropPixelsRef.current = null;
  }, [coverImages.length]);

  useEffect(() => {
    if (!open || rawImageSrc) return;
    const onPaste = (e: ClipboardEvent) => {
      if (coverImages.length >= ASSIGNMENT_COVER_IMAGE_MAX) {
        setError(
          `과제 이미지는 최대 ${ASSIGNMENT_COVER_IMAGE_MAX}개까지 등록할 수 있습니다. 기존 이미지를 삭제한 뒤 올려 주세요.`
        );
        return;
      }
      const blob = readClipboardImageBlob(e.clipboardData);
      if (!blob) return;
      e.preventDefault();
      e.stopPropagation();
      const file =
        blob instanceof File
          ? blob
          : new File([blob], 'clipboard-image.png', {
              type: blob.type || 'image/png',
            });
      void handleFile(file);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open, rawImageSrc, coverImages.length, handleFile]);

  const handleDrop: React.DragEventHandler<HTMLDivElement> = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await handleFile(file);
  };

  const handleSaveClick = async () => {
    if (!rawImageSrc || !cropPixelsRef.current || isSaving) return;
    if (coverImages.length >= ASSIGNMENT_COVER_IMAGE_MAX) {
      setError(`과제 이미지는 최대 ${ASSIGNMENT_COVER_IMAGE_MAX}개까지 등록할 수 있습니다.`);
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      const blob = await cropRectToWebp(rawImageSrc, cropPixelsRef.current, OUT_W, OUT_H, 0.78);
      const uploaded = await uploadAssignmentCoverImage(blob);
      const image: AssignmentCoverImage = {
        id: newAssignmentCoverImageId(),
        path: uploaded.path,
        url: uploaded.url,
        createdAt: new Date().toISOString(),
      };
      void putAssignmentCoverCacheFromBlob(image, blob);
      onUploaded(image);
      onClose();
    } catch (e) {
      console.error(e);
      const msg = e instanceof Error ? e.message : '';
      setError(
        msg === 'QUOTA_EXCEEDED'
          ? '이미지 저장 용량이 부족합니다. 사용량을 확인한 뒤 다시 시도해 주세요.'
          : '이미지 저장에 실패했습니다. 다시 시도해 주세요.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteLibrary = async (image: AssignmentCoverImage, e: React.MouseEvent) => {
    e.stopPropagation();
    if (deletingId) return;
    if (!window.confirm('이 과제 이미지를 라이브러리에서 삭제할까요?')) return;
    setDeletingId(image.id);
    setError('');
    try {
      await deleteAssignmentCoverImage(image.path);
      await invalidateAssignmentCoverCache(image.id);
      onDeleteLibraryImage(image.id);
    } catch (err) {
      console.error(err);
      setError('이미지 삭제에 실패했습니다.');
    } finally {
      setDeletingId(null);
    }
  };

  if (!open) return null;

  const atLimit = coverImages.length >= ASSIGNMENT_COVER_IMAGE_MAX;

  return createPortal(
    <div
      className="cp-member-photo-upload-overlay cp-apn-cover-upload-overlay"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      role="presentation"
    >
      <div
        className="cp-member-photo-upload-modal cp-apn-cover-upload-modal"
        role="dialog"
        aria-modal="true"
        aria-label="과제 이미지 설정"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-member-photo-upload-header">
          <h3>과제 이미지 설정</h3>
          <button type="button" className="cp-member-photo-upload-close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>

        {error ? (
          <p className="cp-member-photo-upload-error" role="alert">
            {error}
          </p>
        ) : null}

        {!rawImageSrc ? (
          <div
            className={`cp-member-photo-upload-dropzone${isDragging ? ' is-dragging' : ''}${atLimit ? ' is-disabled' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              if (!atLimit) setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => {
              if (atLimit) {
                setError(`과제 이미지는 최대 ${ASSIGNMENT_COVER_IMAGE_MAX}개까지 등록할 수 있습니다. 기존 이미지를 삭제한 뒤 올려 주세요.`);
                return;
              }
              fileInputRef.current?.click();
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (!atLimit) fileInputRef.current?.click();
              }
            }}
          >
            <span className="cp-member-photo-upload-drop-plus" aria-hidden>
              +
            </span>
            <p className="cp-member-photo-upload-drop-text">
              {atLimit
                ? `등록 한도 ${ASSIGNMENT_COVER_IMAGE_MAX}개에 도달했습니다`
                : '이미지를 드래그·클릭하거나 Ctrl+V로 붙여넣기'}
            </p>
            <p className="cp-member-photo-upload-drop-hint">과제 블럭 비율에 맞춰 자를 수 있습니다 · JPG/PNG/WebP · 10MB 이하</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="cp-member-photo-upload-file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void handleFile(file);
              }}
            />
          </div>
        ) : (
          <div className="cp-member-photo-upload-crop-wrap">
            <div className="cp-member-photo-upload-crop cp-apn-cover-upload-crop">
              <Cropper
                image={rawImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={ASSIGNMENT_COVER_ASPECT}
                cropShape="rect"
                showGrid
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_area, pixels) => {
                  cropPixelsRef.current = pixels;
                }}
              />
            </div>
            <label className="cp-member-photo-upload-zoom">
              <span>확대</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
            </label>
            <div className="cp-member-photo-upload-footer">
              <button
                type="button"
                className="cp-member-photo-upload-btn is-cancel"
                onClick={() => {
                  setRawImageSrc('');
                  cropPixelsRef.current = null;
                }}
                disabled={isSaving}
              >
                다시 선택
              </button>
              <button
                type="button"
                className="cp-member-photo-upload-btn is-save"
                onClick={() => void handleSaveClick()}
                disabled={isSaving}
              >
                {isSaving ? '저장 중…' : '등록'}
              </button>
            </div>
          </div>
        )}

        <div className="cp-apn-cover-library">
          <div className="cp-apn-cover-library-head">
            <span className="cp-apn-cover-library-title">등록된 이미지</span>
            <span className="cp-apn-cover-library-count">
              {coverImages.length}/{ASSIGNMENT_COVER_IMAGE_MAX}
            </span>
          </div>
          {coverImages.length === 0 ? (
            <p className="cp-apn-cover-library-empty">아직 등록된 이미지가 없습니다.</p>
          ) : (
            <div className="cp-apn-cover-library-grid">
              {coverImages.map((img) => (
                <button
                  key={img.id}
                  type="button"
                  className={`cp-apn-cover-library-thumb${
                    selectedCoverImageId === img.id ? ' is-selected' : ''
                  }`}
                  onClick={() => {
                    onSelectExisting(img.id);
                    onClose();
                  }}
                  aria-label="등록된 과제 이미지 선택"
                  aria-pressed={selectedCoverImageId === img.id}
                >
                  <CachedAssignmentCoverImage image={img} alt="" />
                  <span
                    className="cp-apn-cover-library-trash"
                    role="button"
                    tabIndex={0}
                    aria-label="이미지 삭제"
                    onClick={(e) => void handleDeleteLibrary(img, e)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        void handleDeleteLibrary(img, e as unknown as React.MouseEvent);
                      }
                    }}
                  >
                    {deletingId === img.id ? '…' : (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
                        <path
                          d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AssignmentCoverImageModal;
